import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button, Input, notification, Select, Table, Tag, Tooltip } from 'antd';
import dayjs from 'dayjs';

import { BiExport } from 'react-icons/bi';
import { FaEye, FaFilter, FaSearch } from 'react-icons/fa';
import { HiOutlineRefresh } from 'react-icons/hi';

import '../../../css/UserAdmin/Global.css';

import Footer from '../Footer';
import Header from '../NewHeader';
import SideNav from '../SideNav';

const ManageUserLedger = () => {
    const navigate = useNavigate();
    const [isSideNavOpen, setIsSideNavOpen] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth < 768);

    const [allEnrollments, setAllEnrollments] = useState([]); // Stores all OfficialApplicantEnrolled records
    const [dataSource, setDataSource] = useState([]);       // Data for the table after filtering
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Filters for the table
    const [academicYears, setAcademicYears] = useState([]);
    const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);
    const [academicTerms, setAcademicTerms] = useState([]);
    const [selectedAcademicTerm, setSelectedAcademicTerm] = useState(null);
    const [paymentStatuses, setPaymentStatuses] = useState([]); // e.g., ['Fully Paid', 'Partial Payment', 'Pending']
    const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);

    // AntD controlled table filters and sorter
    const [tableFilters, setTableFilters] = useState({});
    const [sorter, setSorter] = useState({});

    const toggleSideNav = useCallback(() => setIsSideNavOpen(prev => !prev), []);

    useEffect(() => {
        const handleResize = () => setIsSmallScreen(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        handleResize();
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // --- Fetch Enrolled Students Data ---
    const fetchEnrollmentsForLedger = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/enrollments`);
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
            }
            const result = await response.json();
            const enrollmentsData = Array.isArray(result) ? result : (result.data || []);

            if (!Array.isArray(enrollmentsData)) {
                throw new Error(`Expected array but got ${typeof enrollmentsData}`);
            }

            // Pre-process to add key and potentially a computed payment status for easier filtering
            const processedData = enrollmentsData.map(student => {
                let computedPaymentStatus = 'N/A';
                let nextPaymentDueDate = null;
                let nextPaymentAmount = null;

                if (student.paymentInfo) {
                    // --- Computed Payment Status Logic (same as before) ---
                    if (student.paymentInfo.isFullPayment) {
                        computedPaymentStatus = 'Fully Paid';
                    } else if (student.paymentInfo.paymentAmount > 0 && student.paymentInfo.remainingPayments && student.paymentInfo.remainingPayments.length > 0) {
                        // Filter for pending payments
                        const pendingPayments = student.paymentInfo.remainingPayments.filter(
                            p => !p.status || p.status.toLowerCase() !== 'paid'
                        );
                        if (pendingPayments.length > 0) {
                            computedPaymentStatus = 'Partial Payment';
                        } else {
                            // All remaining payments are marked paid, or no remaining payments after initial
                            computedPaymentStatus = 'Fully Paid';
                        }
                    } else if (student.paymentInfo.paymentAmount > 0) {
                        computedPaymentStatus = 'Fully Paid'; // Or 'Initial Payment Made'
                    }
                    else {
                        computedPaymentStatus = 'Pending Payment';
                    }

                    // --- Determine Next Payment Logic ---
                    if (student.paymentInfo.remainingPayments && student.paymentInfo.remainingPayments.length > 0) {
                        const today = dayjs().startOf('day'); // Get start of today for comparison
                        const futurePendingPayments = student.paymentInfo.remainingPayments
                            .filter(p => (!p.status || p.status.toLowerCase() !== 'paid') && dayjs(p.dueDate).isValid() && dayjs(p.dueDate).isSameOrAfter(today))
                            .sort((a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix()); // Sort by due date ascending

                        if (futurePendingPayments.length > 0) {
                            nextPaymentDueDate = futurePendingPayments[0].dueDate;
                            nextPaymentAmount = futurePendingPayments[0].amount;
                        }
                    }
                }
                const isOfficiallyEnrolled = student.enrollmentStatus === 'Complete';

                return {
                    ...student,
                    key: student._id,
                    computedPaymentStatus,
                    isOfficiallyEnrolled,
                    nextPaymentDueDate,
                    nextPaymentAmount,
                };
            });

            setAllEnrollments(processedData);

            // ... (populate filter dropdowns - no change here) ...
            const uniqueYears = [...new Set(processedData.map(item => item.academicYear))].filter(Boolean).sort().reverse();
            const uniqueTerms = [...new Set(processedData.map(item => item.academicTerm))].filter(Boolean).sort();
            const uniquePaymentStatuses = [...new Set(processedData.map(item => item.computedPaymentStatus))].filter(Boolean).sort();

            setAcademicYears(uniqueYears);
            setAcademicTerms(uniqueTerms);
            setPaymentStatuses(uniquePaymentStatuses);

            if (uniqueYears.length > 0 && !selectedAcademicYear) setSelectedAcademicYear(uniqueYears[0]);
            if (uniqueTerms.length > 0 && !selectedAcademicTerm) setSelectedAcademicTerm(uniqueTerms[0]);


        } catch (error) {
            // ... (error handling) ...
        } finally {
            setLoading(false);
        }
    }, [selectedAcademicYear, selectedAcademicTerm]); // Dependencies for re-evaluating defaults

    useEffect(() => {
        fetchEnrollmentsForLedger();
    }, [fetchEnrollmentsForLedger]);


    // --- Client-Side Filtering and Sorting ---
    const filteredStudentsForLedger = useMemo(() => {
        return allEnrollments.filter(student => {
            // Filter out students who are not officially enrolled
            if (!student.isOfficiallyEnrolled) {
                return false;
            }
            if (selectedAcademicYear && student.academicYear !== selectedAcademicYear) return false;
            if (selectedAcademicTerm && student.academicTerm !== selectedAcademicTerm) return false;
            if (selectedPaymentStatus && student.computedPaymentStatus !== selectedPaymentStatus) return false;

            if (searchTerm) {
                const lowerSearchTerm = searchTerm.toLowerCase();
                return (
                    (student.studentID || '').toLowerCase().includes(lowerSearchTerm) ||
                    (student.fullName || '').toLowerCase().includes(lowerSearchTerm) ||
                    (student.email || '').toLowerCase().includes(lowerSearchTerm) ||
                    (student.paymentInfo?.referenceNumber || '').toLowerCase().includes(lowerSearchTerm)
                );
            }
            return true;
        });
    }, [allEnrollments, searchTerm, selectedAcademicYear, selectedAcademicTerm, selectedPaymentStatus]);

    useEffect(() => {
        // Apply AntD table-controlled sorting and filtering after our custom filters
        let dataToDisplay = [...filteredStudentsForLedger];

        // AntD Table Column Filters (Example: enrollmentStatus from student record if needed)
        if (tableFilters.enrollmentStatus && tableFilters.enrollmentStatus.length > 0) {
            dataToDisplay = dataToDisplay.filter(student =>
                tableFilters.enrollmentStatus.includes(student.enrollmentStatus)
            );
        }
        // Add other AntD column filter logic here

        // AntD Table Sorter
        if (sorter.columnKey && sorter.order) {
            dataToDisplay.sort((a, b) => {
                let valA = a[sorter.columnKey];
                let valB = b[sorter.columnKey];

                // Handle nested properties like paymentInfo.referenceNumber
                if (sorter.columnKey === 'paymentRef') {
                    valA = a.paymentInfo?.referenceNumber || '';
                    valB = b.paymentInfo?.referenceNumber || '';
                }
                if (sorter.columnKey === 'totalAmountDue') { // For computed column
                    valA = a.paymentInfo?.tuitionAndFees?.totalAmount || 0;
                    valB = b.paymentInfo?.tuitionAndFees?.totalAmount || 0;
                }
                if (sorter.columnKey === 'amountPaid') { // For computed column
                    valA = a.paymentInfo?.paymentAmount || 0;
                    valB = b.paymentInfo?.paymentAmount || 0;
                }


                if (typeof valA === 'string' && typeof valB === 'string') {
                    return sorter.order === 'ascend' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                }
                if (typeof valA === 'number' && typeof valB === 'number') {
                    return sorter.order === 'ascend' ? valA - valB : valB - valA;
                }
                // Fallback for other types or mixed types
                const strA = String(valA).toLowerCase();
                const strB = String(valB).toLowerCase();
                return sorter.order === 'ascend' ? strA.localeCompare(strB) : strB.localeCompare(strA);
            });
        }
        setDataSource(dataToDisplay);
    }, [filteredStudentsForLedger, tableFilters, sorter]);


    const handleSearch = (value) => setSearchTerm(value);

    const handleClearFilters = () => {
        setSearchTerm('');
        setSelectedAcademicYear(academicYears.length > 0 ? academicYears[0] : null); // Reset to default or null
        setSelectedAcademicTerm(academicTerms.length > 0 ? academicTerms[0] : null); // Reset to default or null
        setSelectedPaymentStatus(null);
        setTableFilters({});
        setSorter({});
    };

    const handleRefresh = () => {
        fetchEnrollmentsForLedger(); // This will use current searchTerm if needed by fetch function
    };

    const handleExport = () => {
        // Modify this export to include ledger-specific data if needed
        const currentDate = new Date().toISOString().split('T')[0];
        const fileName = `student-ledger-report-${currentDate}.csv`; // Changed to CSV for simplicity

        // Prepare CSV data
        const headers = ["Student ID", "Full Name", "Email", "Academic Year", "Term", "Level", "Strand", "Section", "Enrollment Status", "Payment Status", "Total Assessed", "Amount Paid", "Balance"];
        const csvData = dataSource.map(student => [
            student.studentID || 'N/A',
            student.fullName || 'N/A',
            student.email || 'N/A',
            student.academicYear || 'N/A',
            student.academicTerm || 'N/A',
            student.academicLevel || 'N/A',
            student.academicStrand || 'N/A',
            student.section || 'N/A',
            student.enrollmentStatus || 'N/A',
            student.computedPaymentStatus || 'N/A',
            (student.paymentInfo?.tuitionAndFees?.totalAmount || 0).toFixed(2),
            (student.paymentInfo?.paymentAmount || 0).toFixed(2),
            ((student.paymentInfo?.tuitionAndFees?.totalAmount || 0) - (student.paymentInfo?.paymentAmount || 0)).toFixed(2)
        ]);

        let csvContent = "data:text/csv;charset=utf-8," + headers.join(",") + "\n"
            + csvData.map(e => e.join(",")).join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        notification.success({ message: 'Export Successful', description: `${fileName} has been downloaded.` });
        // Log export action (ensure createLog is defined or imported)
        // createLog('Export Ledger', `Exported student ledger report`);
    };

    const columns = [
        {
            title: 'Student ID',
            dataIndex: 'studentID',
            key: 'studentID',
            width: 130,
            sorter: true,
            sortOrder: sorter.columnKey === 'studentID' ? sorter.order : null,
        },
        {
            title: 'Full Name',
            dataIndex: 'fullName',
            key: 'fullName',
            width: 220,
            render: (text, record) => (
                <div>
                    <div style={{ fontWeight: 'bold' }}>{text || 'N/A'}</div>
                    {record.email && (<div style={{ fontSize: '12px', color: '#666' }}>{record.email}</div>)}
                </div>
            ),
            sorter: true,
            sortOrder: sorter.columnKey === 'fullName' ? sorter.order : null,
        },
        {
            title: 'Grade & Section',
            key: 'gradeSection',
            width: 180,
            render: (_, record) => `${record.academicLevel || 'N/A'} - ${record.section || 'N/A'} (${record.academicStrand || 'N/A'})`,
            sorter: (a, b) => {
                const valA = `${a.academicLevel || ''} - ${a.section || ''} (${a.academicStrand || ''})`;
                const valB = `${b.academicLevel || ''} - ${b.section || ''} (${b.academicStrand || ''})`;
                return valA.localeCompare(valB);
            },
            sortOrder: sorter.columnKey === 'gradeSection' ? sorter.order : null,
        },
        {
            title: 'Enroll. Status',
            dataIndex: 'enrollmentStatus',
            key: 'enrollmentStatus',
            width: 130,
            filters: [
                { text: 'Complete', value: 'Complete' }, { text: 'Incomplete', value: 'Incomplete' },
                { text: 'Pending', value: 'Pending' }, { text: 'Withdrawn', value: 'Withdrawn' },
                { text: 'Dropped', value: 'Dropped' },
            ],
            onFilter: (value, record) => record.enrollmentStatus === value,
            filteredValue: tableFilters.enrollmentStatus || null,
            render: (status) => {
                let color = 'default';
                if (status === 'Complete') color = 'green';
                else if (status === 'Incomplete') color = 'orange';
                else if (status === 'Pending') color = 'blue';
                else if (status === 'Withdrawn') color = 'red';
                else if (status === 'Dropped') color = 'volcano';
                return <Tag color={color}>{status || 'N/A'}</Tag>;
            },
            sorter: true,
            sortOrder: sorter.columnKey === 'enrollmentStatus' ? sorter.order : null,
        },
        {
            title: 'Payment Status',
            dataIndex: 'computedPaymentStatus',
            key: 'computedPaymentStatus',
            width: 150,
            filters: paymentStatuses.map(status => ({ text: status, value: status })),
            onFilter: (value, record) => record.computedPaymentStatus === value,
            filteredValue: tableFilters.computedPaymentStatus || null,
            render: (status) => {
                let color = 'default';
                if (status === 'Fully Paid') color = 'success';
                else if (status === 'Partial Payment') color = 'processing';
                else if (status === 'Pending Payment') color = 'error';
                return <Tag color={color}>{status || 'N/A'}</Tag>;
            },
            sorter: true,
            sortOrder: sorter.columnKey === 'computedPaymentStatus' ? sorter.order : null,
        },
        {
            title: 'Total Assessed',
            dataIndex: ['paymentInfo', 'tuitionAndFees', 'totalAmount'],
            key: 'totalAmountDue',
            width: 130,
            align: 'right',
            render: (amount) => amount ? amount.toFixed(2) : '0.00',
            sorter: true,
            sortOrder: sorter.columnKey === 'totalAmountDue' ? sorter.order : null,
        },
        {
            title: 'Amount Paid',
            dataIndex: ['paymentInfo', 'paymentAmount'],
            key: 'amountPaid',
            width: 130,
            align: 'right',
            render: (amount) => amount ? amount.toFixed(2) : '0.00',
            sorter: true,
            sortOrder: sorter.columnKey === 'amountPaid' ? sorter.order : null,
        },
        {
            title: 'Balance',
            key: 'balance',
            width: 130,
            align: 'right',
            render: (_, record) => {
                const total = record.paymentInfo?.tuitionAndFees?.totalAmount || 0;
                const paid = record.paymentInfo?.paymentAmount || 0;
                const balance = total - paid;
                return <span style={{ color: balance > 0 ? 'red' : 'green' }}>{balance.toFixed(2)}</span>;
            },
            sorter: (a, b) => {
                const balanceA = (a.paymentInfo?.tuitionAndFees?.totalAmount || 0) - (a.paymentInfo?.paymentAmount || 0);
                const balanceB = (b.paymentInfo?.tuitionAndFees?.totalAmount || 0) - (b.paymentInfo?.paymentAmount || 0);
                return balanceA - balanceB;
            },
            sortOrder: sorter.columnKey === 'balance' ? sorter.order : null,
        },
        {
            title: 'Next Due Date',
            dataIndex: 'nextPaymentDueDate',
            key: 'nextPaymentDueDate',
            width: 150,
            render: (date) => date ? dayjs(date).format('YYYY-MM-DD') : <Tag>N/A</Tag>,
            sorter: true,
            sortOrder: sorter.columnKey === 'nextPaymentDueDate' ? sorter.order : null,
        },
        {
            title: 'Next Amount Due',
            dataIndex: 'nextPaymentAmount',
            key: 'nextPaymentAmount',
            width: 150,
            align: 'right',
            render: (amount) => amount != null ? amount.toFixed(2) : <Tag>N/A</Tag>,
            sorter: true,
            sortOrder: sorter.columnKey === 'nextPaymentAmount' ? sorter.order : null,
        },
        {
            title: 'Action',
            key: 'action',
            fixed: 'right',
            width: 150,
            align: 'center',
            render: (_, record) => (
                <Tooltip title="View User Ledger Details">
                    <Button
                        icon={<FaEye />}
                        onClick={() => navigate(`/admin/manage-user-ledger/view/${record._id}`)}
                    >
                        View Ledger
                    </Button>
                </Tooltip>
            ),
        },
    ];

    return (
        <div className={`admin-page-container ${isSideNavOpen ? 'admin-sidenav-open' : ''}`}>
            <Header onMenuClick={toggleSideNav} isSideNavOpen={isSideNavOpen} />
            <div className="admin-content-wrapper">
                <div className="admin-sidenav-container">
                    <SideNav isOpen={isSideNavOpen} onClose={toggleSideNav} isSmallScreen={isSmallScreen} />
                </div>
                <main className="admin-main-content-area">
                    <div className="page-title">
                        <p className="heading">Manage User Ledgers</p>
                    </div>

                    <div className="table-functions">
                        <div className="left-tools" style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <Select
                                placeholder="Academic Year"
                                style={{ minWidth: 180 }}
                                value={selectedAcademicYear}
                                onChange={setSelectedAcademicYear}
                                loading={loading && academicYears.length === 0}
                                allowClear
                            >
                                {academicYears.map(year => <Select.Option key={year} value={year}>{year}</Select.Option>)}
                            </Select>
                            <Select
                                placeholder="Term"
                                style={{ minWidth: 120 }}
                                value={selectedAcademicTerm}
                                onChange={setSelectedAcademicTerm}
                                loading={loading && academicTerms.length === 0}
                                allowClear
                                disabled={!selectedAcademicYear}
                            >
                                {academicTerms.map(term => <Select.Option key={term} value={term}>{term}</Select.Option>)}
                            </Select>
                            <Select
                                placeholder="Payment Status"
                                style={{ minWidth: 180 }}
                                value={selectedPaymentStatus}
                                onChange={setSelectedPaymentStatus}
                                allowClear
                            >
                                {paymentStatuses.map(status => <Select.Option key={status} value={status}>{status}</Select.Option>)}
                            </Select>
                            <Button icon={<FaFilter />} onClick={handleClearFilters}>Reset Filters</Button>
                            <Button icon={<HiOutlineRefresh />} onClick={handleRefresh} loading={loading}>Refresh</Button>
                            <Button icon={<BiExport />} onClick={handleExport}>Export CSV</Button>
                        </div>
                        <div className="right-tools">
                            <Input
                                placeholder="Search Student ID, Name, Payment Ref..."
                                allowClear
                                style={{ width: 300 }}
                                value={searchTerm}
                                onChange={(e) => handleSearch(e.target.value)}
                                suffix={<FaSearch style={{ color: '#aaa' }} />}
                            />
                        </div>
                    </div>

                    <Table
                        style={{ width: '100%', flex: 1, marginTop: '16px' }}
                        columns={columns}
                        dataSource={dataSource}
                        loading={loading}
                        bordered
                        scroll={{ x: 1700 }}
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} students`,
                            pageSizeOptions: ['10', '20', '50', '100'],
                        }}
                        onChange={(pagination, filters, newSorter) => {
                            setTableFilters(filters);
                            setSorter(newSorter);
                        }}
                    />
                </main>
            </div>
            <Footer />
        </div>
    );
};

export default ManageUserLedger;